import { useQuery } from '@tanstack/react-query';
import Trans from 'next-translate/Trans';
import useTranslation from 'next-translate/useTranslation';
import { memo, useEffect, useState, useCallback } from 'react';

import { Box, Grid, Pagination, Typography } from '@core/component';
import { ARTICLES_TO_SHOW, Translate } from '@core/constant';
import { useContextMediaQuery, useContextRouting, useContextAlternate } from '@core/context';
import { useLanguage } from '@core/hook';
import { getSanityQuery } from '@core/sanity';
import {
  EActionAttribute,
  EColor,
  EFontWeight,
  ETypographyVariant,
  ViewCMSProps,
} from '@core/type';
import { SanityBlogSearch, SanityBlogSearchArticles } from '@core/type/sanity';
import { getLanguageFromTags } from '@core/util';
import { SpotlightBanner } from '@core/widget';

import { BlogArticle } from './BlogArticle';

export const ViewBlogSearchList = memo(
  ({ cms: { articlesCount } }: ViewCMSProps<SanityBlogSearch>) => {
    const { t: tBlog } = useTranslation(Translate.page.BLOG_HOME);
    const { query, setQuery } = useContextRouting();
    const { isPhone } = useContextMediaQuery();
    const { language } = useLanguage();
    const { setAlternates } = useContextAlternate();

    const [isSubscriptionSuccessful, setIsSubscriptionSuccessful] = useState(false);

    const { data: blogArticles } = useQuery({
      ...getSanityQuery<SanityBlogSearchArticles>('blogSearchArticlesQuery', {
        language: getLanguageFromTags(language),
        page: query.page as string,
        perPage: ARTICLES_TO_SHOW,
        q: query.q as string,
      }),
      keepPreviousData: true,
    });

    const changePage = useCallback(
      (page: number) => {
        const q = { q: query.q };
        setQuery(page === 1 ? q : { page: page.toString(), ...q });
      },
      [language, query.q],
    );

    useEffect(() => {
      if (query.page && blogArticles?.length <= 0) {
        changePage(1);
      }
      setAlternates();
    }, [blogArticles]);

    return (
      <>
        <Box marginBottom={'32px'} display={'flex'}>
          <Typography as={EActionAttribute.SPAN} fontSize={'15px'} marginRight={'8px'}>
            <Trans
              i18nKey={`${Translate.page.BLOG_HOME}:searchResult`}
              components={{
                strong: <Typography as={EActionAttribute.SPAN} fontWeight={EFontWeight.BOLD} />,
              }}
              values={{ query: query?.q }}
            />
          </Typography>

          <Typography as={EActionAttribute.SPAN} fontSize={'15px'} color={EColor.INFO}>
            <Trans
              i18nKey={`${Translate.page.BLOG_HOME}:${
                blogArticles?.length === 1 ? 'oneArticle' : 'manyArticles'
              }`}
              values={{ count: articlesCount }}
            />
          </Typography>
        </Box>

        {blogArticles?.length > 0 ? (
          <Box paddingBottom={isPhone ? '104px' : isSubscriptionSuccessful ? '224px' : '144px'}>
            <Grid isContainer rowSpacing={64} columnSpacing={30}>
              {blogArticles.map((article) => (
                <Grid xs={12} md={6} lg={4} key={article.pageSlug?.slug.current}>
                  <BlogArticle article={article} />
                </Grid>
              ))}
            </Grid>

            <Box marginTop={isPhone ? '56px' : '80px'}>
              <Pagination
                page={Number(query?.page) || 1}
                perPage={ARTICLES_TO_SHOW}
                itemsCount={articlesCount}
                onPageChange={changePage}
              />
            </Box>
          </Box>
        ) : (
          <Box padding={'144px 0'} margin={'0 auto'} maxWidth={'410px'}>
            <Typography
              variant={ETypographyVariant.BODY1}
              color={EColor.SECONDARY}
              textAlign={'center'}
            >
              {tBlog('emptyResult')}
            </Typography>
          </Box>
        )}

        {!isSubscriptionSuccessful && <SpotlightBanner fn={setIsSubscriptionSuccessful} />}
      </>
    );
  },
);

ViewBlogSearchList.displayName = 'ViewBlogSearchList';
